import React from "react";
import SbEditable from "storyblok-react";
import classnames from "classnames";
import Button from "@/components/Button/button";
import { CheckCircleIcon, InfoIcon, MinusIcon } from "@phosphor-icons/react";
import { usePricingBilling } from "@/components/PricingBillingContext";
import styles from "./pricing-comparison-table.module.scss";
import type {
  PricingComparisonTableStoryblok,
  PricingComparisonColumnStoryblok,
  PricingComparisonRowStoryblok,
  ComparisonCellType,
} from "./types";

interface Props {
  blok: PricingComparisonTableStoryblok;
}

const CellContent = ({
  type,
  text,
}: {
  type: ComparisonCellType;
  text?: string;
}) => {
  if (type === "included") {
    return (
      <span className={styles.iconIncluded} aria-label="Included">
        <CheckCircleIcon size={20} weight="fill" aria-hidden="true" />
      </span>
    );
  }

  if (type === "text" && text) {
    return <span className={styles.cellTextValue}>{text}</span>;
  }

  return (
    <span className={styles.iconExcluded} aria-label="Not included">
      <MinusIcon size={20} weight="bold" aria-hidden="true" />
    </span>
  );
};

const Tooltip = ({ text }: { text: string }) => (
  <button type="button" className={styles.tooltipTrigger} aria-label={text}>
    <InfoIcon size={20} weight="bold" aria-hidden="true" />
    <span className={styles.tooltipContent}>{text}</span>
  </button>
);

const PricingComparisonTable = ({ blok }: Props) => {
  const sharedBilling = usePricingBilling();

  const billingPeriod = sharedBilling?.billingPeriod ?? "monthly";

  const columns = Array.isArray(blok.columns) ? blok.columns : [];
  const rows = Array.isArray(blok.rows) ? blok.rows : [];
  const hasColumns = columns.length > 0;
  const rowsBeforeLast = rows.slice(0, -1);
  const lastRow = rows.length ? rows[rows.length - 1] : null;
  const highlightIndex = columns.findIndex((col) => col.highlight);

  const getCell = (
    row: PricingComparisonRowStoryblok,
    colIndex: number
  ): { type: ComparisonCellType; text?: string } => {
    const cells = row.cells || [];
    if (colIndex >= cells.length) {
      return { type: "excluded" };
    }
    return {
      type: cells[colIndex].type || "excluded",
      text: cells[colIndex].text,
    };
  };

  const renderDesktopRow = (
    row: PricingComparisonRowStoryblok,
    rowIndex: number
  ) => {
    const isOdd = rowIndex % 2 !== 0;

    return (
      <div
        key={row._uid}
        className={classnames(styles.bodyRow, {
          [styles.bodyRowOdd]: isOdd,
          [styles.bodyRowEven]: !isOdd,
        })}
      >
        <div className={styles.featureCell}>
          <span className={styles.featureLabel}>{row.feature}</span>
          {row.tooltip ? <Tooltip text={row.tooltip} /> : null}
        </div>

        {columns.map((col, colIndex) => {
          const cell = getCell(row, colIndex);
          const isHighlight = colIndex === highlightIndex;

          return (
            <React.Fragment key={col._uid}>
              <div className={styles.verticalDivider} />
              <div
                className={classnames(styles.dataCell, {
                  [styles.dataCellHighlight]: isHighlight && !isOdd,
                  [styles.dataCellHighlightOdd]: isHighlight && isOdd,
                })}
              >
                <CellContent type={cell.type} text={cell.text} />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const renderMobileRow = (row: PricingComparisonRowStoryblok) => (
    <div key={row._uid} className={styles.mobileRowWrapper}>
      <div className={styles.mobileFeatureLabel}>
        <span className={styles.mobileFeatureLabelText}>{row.feature}</span>
        {row.tooltip ? <Tooltip text={row.tooltip} /> : null}
      </div>
      <div className={styles.mobileValuesRow}>
        {columns.map((col, colIndex) => {
          const cell = getCell(row, colIndex);
          return (
            <React.Fragment key={col._uid}>
              {colIndex > 0 ? (
                <div className={styles.mobileValueDivider} />
              ) : null}
              <div
                className={classnames(styles.mobileDataCell, {
                  [styles.mobileDataCellHighlight]: colIndex === highlightIndex,
                })}
              >
                <CellContent type={cell.type} text={cell.text} />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  return (
    // @ts-expect-error
    <SbEditable content={blok}>
      <div className={styles.comparisonSection}>
        {blok.heading ? (
          <h2 className={styles.heading}>{blok.heading}</h2>
        ) : null}

        {hasColumns ? (
          <>
            {/* Desktop table */}
            <div className={styles.desktopTable}>
              <div className={styles.table}>
                <div className={styles.stickyGroup}>
                  <div className={styles.headerRow}>
                    <div className={styles.headerLabelCell} />

                    {columns.map((col, colIndex) => (
                      <React.Fragment key={col._uid}>
                        <div className={styles.verticalDivider} />
                        <div
                          className={classnames(styles.headerColumnCell, {
                            [styles.headerColumnCellHighlight]:
                              colIndex === highlightIndex,
                          })}
                        >
                          <p className={styles.headerPlanName}>{col.name}</p>
                          <div className={styles.headerCtaWrapper}>
                            <ColumnCta
                              col={col}
                              billingPeriod={billingPeriod}
                              isHighlighted={colIndex === highlightIndex}
                            />
                            <ColumnPrice
                              col={col}
                              billingPeriod={billingPeriod}
                            />
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>

                  {rowsBeforeLast.map((row, rowIndex) =>
                    renderDesktopRow(row, rowIndex)
                  )}
                </div>

                {lastRow ? renderDesktopRow(lastRow, rows.length - 1) : null}
              </div>
            </div>

            {/* Mobile table */}
            <div className={styles.mobileTable}>
              <div className={styles.stickyGroup}>
                <div className={styles.mobileHeader}>
                  {columns.map((col, colIndex) => (
                    <React.Fragment key={col._uid}>
                      {colIndex > 0 ? (
                        <div className={styles.mobileHeaderDivider} />
                      ) : null}
                      <div
                        className={classnames(styles.mobileHeaderCell, {
                          [styles.mobileHeaderCellHighlight]:
                            colIndex === highlightIndex,
                        })}
                      >
                        <p className={styles.mobileHeaderPlanName}>
                          {col.name}
                        </p>
                        <ColumnCta
                          col={col}
                          billingPeriod={billingPeriod}
                          isHighlighted={colIndex === highlightIndex}
                        />
                        <ColumnPrice
                          col={col}
                          billingPeriod={billingPeriod}
                          compact
                        />
                      </div>
                    </React.Fragment>
                  ))}
                </div>

                {rowsBeforeLast.map((row) => renderMobileRow(row))}
              </div>

              {lastRow ? renderMobileRow(lastRow) : null}
            </div>
          </>
        ) : null}
      </div>
    </SbEditable>
  );
};

const ColumnCta = ({
  col,
  billingPeriod,
  isHighlighted,
}: {
  col: PricingComparisonColumnStoryblok;
  billingPeriod: "monthly" | "yearly";
  isHighlighted: boolean;
}) => {
  const ctaLink =
    billingPeriod === "monthly" ? col.cta_link_monthly : col.cta_link_yearly;
  if (!ctaLink) return null;

  return (
    <Button
      link={ctaLink}
      btnStyle={isHighlighted ? "black" : "border"}
      size="small"
      className={
        isHighlighted ? styles.headerCtaButtonHighlight : styles.headerCtaButton
      }
    >
      {col.cta_text || "Sign up"}
    </Button>
  );
};

const ColumnPrice = ({
  col,
  billingPeriod,
  compact = false,
}: {
  col: PricingComparisonColumnStoryblok;
  billingPeriod: "monthly" | "yearly";
  compact?: boolean;
}) => {
  const price =
    billingPeriod === "monthly" ? col.price_monthly : col.price_yearly;
  if (!price) return null;

  const suffix = compact ? "/mo" : "/month";

  return (
    <div className={styles.headerPrice}>
      <span className={styles.headerPriceAmount}>{price}</span>
      <span className={styles.headerPricePer}>{suffix}</span>
    </div>
  );
};

export default PricingComparisonTable;
